import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React from 'react';
import { Event } from '../../../../types/Event';
import { getWeekRangeFromDate } from '../../../../utility/dateUtils';
import PreferencePill from '../../../preferences/PreferencePill';
import { weeklyRoleFooterContainer, weeklyRoleFooterDayCell, weeklyRoleFooterRow, weeklyRoleFooterTotalSeparator } from './eventCalendarStyles';

type RoleTotal = { quantity: number; color?: string };
type DayTotals = Record<string, Record<string, RoleTotal>>;

interface WeeklyRoleFooterProps {
  events: Event[];
  anchorDate: Date;
}

const WeeklyRoleFooter: React.FC<WeeklyRoleFooterProps> = ({ events, anchorDate }) => {
  // Derive week range from anchor date
  const weekRange = React.useMemo(() => getWeekRangeFromDate(anchorDate), [anchorDate]);

  // Build role totals grouped by calendar day
  const roleTotalsByDay: DayTotals = React.useMemo(
    () =>
      _.chain(events)
        // Ignore events without employee requirements
        .filter((e) => !_.isEmpty(e.employee_requirements))
        // Expand events into per-day role rows
        .flatMap((event) => {
          const start = DateTime.fromISO(event.start_at).toLocal().startOf('day');
          const end = DateTime.fromISO(event.end_at).toLocal().startOf('day');

          return _.flatMap(_.range(0, Math.max(1, end.diff(start, 'days').days)), (offset) =>
            event.employee_requirements!.map((req) => ({
              dayKey: start.plus({ days: offset }).toISODate()!,
              role: req.employee_role_value,
              quantity: req.quantity,
              color: req.employee_role_color,
            }))
          );
        })
        // Group rows by calendar day
        .groupBy('dayKey')
        // Sum quantities per role while keeping color
        .mapValues((rows) =>
          _.mapValues(_.groupBy(rows, 'role'), (items) => ({
            quantity: _.sumBy(items, 'quantity'),
            color: items.find((i) => i.color)?.color,
          }))
        )
        .value(),
    [events]
  );

  return (
    <Box sx={weeklyRoleFooterContainer}>
      <Box /> {/* Time gutter spacer */}
      {weekRange.map((date) => {
        const dayKey = DateTime.fromJSDate(date).toISODate()!;
        const roles = roleTotalsByDay[dayKey] ?? {};
        const total = _.sumBy(Object.values(roles), 'quantity');

        return (
          <Box key={dayKey} sx={weeklyRoleFooterDayCell}>
            {_.isEmpty(roles) ? (
              <Typography variant="caption" color="text.disabled">
                —
              </Typography>
            ) : (
              <>
                {/* Render role rows */}
                {Object.entries(roles).map(([role, { quantity, color }]) => (
                  <Box key={role} sx={weeklyRoleFooterRow}>
                    <Typography sx={{ minWidth: 30, fontWeight: 600 }}>{quantity}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>-</Typography>
                    <PreferencePill value={role} color={color ?? ''} />
                  </Box>
                ))}

                {/* Render total row with separator */}
                <Box sx={weeklyRoleFooterTotalSeparator}>
                  <Box sx={weeklyRoleFooterRow}>
                    <Typography sx={{ minWidth: 30, fontWeight: 600 }}>{total}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>-</Typography>
                    <Typography fontWeight={600}>Total</Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default WeeklyRoleFooter;
